import React from 'react';
import { Layout, TrendingUp, Users, Eye, Map } from 'lucide-react';

const StoreDesign: React.FC = () => {
  const layouts = [
    {
      name: 'Current Layout',
      zones: 8,
      efficiency: 78,
      conversionRate: 24,
      avgDwell: 18
    },
    {
      name: 'Recommended Layout A',
      zones: 9,
      efficiency: 85,
      conversionRate: 28,
      avgDwell: 21
    }
  ];

  const zonePerformance = [
    { zone: 'Entry Display', traffic: 245, conversionRate: 18, efficiency: 72 },
    { zone: 'Women\'s Section', traffic: 198, conversionRate: 32, efficiency: 88 },
    { zone: 'Fitting Rooms', traffic: 87, conversionRate: 45, efficiency: 92 },
    { zone: 'Checkout Area', traffic: 156, conversionRate: 95, efficiency: 98 }
  ];

  const recommendations = [
    {
      title: 'Optimize Product Placement',
      description: 'Move high-margin items to high-traffic zones near entry',
      impact: 'High',
      estimatedLift: '+12% revenue'
    },
    {
      title: 'Expand Fitting Room Area',
      description: 'Add 2 more fitting rooms to reduce wait times',
      impact: 'Medium',
      estimatedLift: '+8% conversion'
    },
    {
      title: 'Reconfigure Sale Section',
      description: 'Position sale items at store rear to increase path length',
      impact: 'Medium',
      estimatedLift: '+15% dwell time'
    }
  ];

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Store Design Analytics
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Optimize layout and merchandising based on customer behavior
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {layouts.map((layout, idx) => (
          <div key={idx} className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--sca-ink)' }}>
                {layout.name}
              </h3>
              <Layout size={24} style={{ color: 'var(--sca-teal)' }} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
                  {layout.efficiency}%
                </div>
                <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  Layout Efficiency
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
                  {layout.conversionRate}%
                </div>
                <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  Conversion Rate
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
                  {layout.zones}
                </div>
                <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  Active Zones
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
                  {layout.avgDwell}m
                </div>
                <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  Avg. Dwell Time
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Zone Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--sca-border)' }}>
                <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Zone
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Traffic
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Conversion
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>
                  Efficiency
                </th>
              </tr>
            </thead>
            <tbody>
              {zonePerformance.map((zone, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--sca-border)' }}>
                  <td className="py-3 px-2 text-sm" style={{ color: 'var(--sca-ink)' }}>
                    {zone.zone}
                  </td>
                  <td className="py-3 px-2 text-sm text-center" style={{ color: 'var(--sca-muted)' }}>
                    {zone.traffic}
                  </td>
                  <td className="py-3 px-2 text-sm text-center font-semibold" style={{ color: 'var(--sca-teal)' }}>
                    {zone.conversionRate}%
                  </td>
                  <td className="py-3 px-2 text-sm text-center">
                    <span
                      className="px-2 py-1 rounded-full text-xs"
                      style={{
                        backgroundColor: 'var(--sca-badge-bg)',
                        color: 'var(--sca-teal)'
                      }}
                    >
                      {zone.efficiency}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Design Recommendations
        </h3>
        <div className="space-y-3">
          {recommendations.map((rec, idx) => (
            <div
              key={idx}
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                    {rec.title}
                  </div>
                  <div className="text-sm mb-2" style={{ color: 'var(--sca-muted)' }}>
                    {rec.description}
                  </div>
                  <div className="text-xs font-medium" style={{ color: 'var(--sca-teal)' }}>
                    Estimated lift: {rec.estimatedLift}
                  </div>
                </div>
                <span
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{
                    backgroundColor: rec.impact === 'High' ? 'var(--sca-badge-bg)' : 'var(--sca-warm-chip)',
                    color: rec.impact === 'High' ? 'var(--sca-teal)' : 'var(--sca-warm-text)'
                  }}
                >
                  {rec.impact} Impact
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StoreDesign;
